import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, XCircle, Spinner, Crown } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [status, setStatus] = useState("polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) {
        setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-16 grid-bg">
      <div className="w-full max-w-md surface border border-white/10 p-10 text-center">
        {status === "polling" && (
          <>
            <Spinner size={40} className="mx-auto animate-spin text-primary-yellow" />
            <h1 className="font-heading text-3xl uppercase font-black mt-6">Confirming payment</h1>
            <p className="text-white/60 mt-3 text-sm">Hold tight — verifying with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 grid place-items-center bg-primary-yellow text-black rounded-full">
              <Crown size={32} weight="fill" />
            </div>
            <h1 data-testid="payment-success-premier" className="font-heading text-4xl uppercase font-black mt-6">You&apos;re Premier</h1>
            <p className="text-white/70 mt-3 text-sm">
              Full season fixtures, unlimited private leagues, and detailed stats are unlocked.
            </p>
            {user?.premier_expires_at && (
              <p className="text-[10px] text-white/40 mt-2 uppercase tracking-widest">
                Active until {new Date(user.premier_expires_at).toLocaleDateString()}
              </p>
            )}
            <div className="mt-8 grid grid-cols-2 gap-2">
              <button
                data-testid="payment-success-cta"
                onClick={() => navigate("/fixtures")}
                className="bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-4 text-sm hover:-translate-y-0.5 transition-transform"
              >
                Make Picks
              </button>
              <button
                onClick={() => navigate("/leagues")}
                className="border border-white/15 uppercase tracking-[0.2em] font-bold py-4 text-sm hover:border-primary-yellow hover:text-primary-yellow transition-colors"
              >
                New League
              </button>
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-red-400" />
            <h1 className="font-heading text-3xl uppercase font-black mt-6">Payment not confirmed</h1>
            <p className="text-white/60 mt-3 text-sm">
              {status === "expired" ? "Checkout session expired." : "We couldn't confirm in time. Check your email or try again."}
            </p>
            <button
              data-testid="payment-retry-cta"
              onClick={() => navigate("/pricing")}
              className="mt-8 w-full border border-white/20 uppercase tracking-[0.2em] font-bold py-4 text-sm hover:border-primary-yellow hover:text-primary-yellow transition-colors"
            >
              Back to Pricing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
